import { useCallback, useEffect, useState } from 'react';

interface UseCooldownOptions {
    initialSeconds?: number;
    seconds: number;
}

const normalizeSeconds = (seconds: number) => Math.max(0, Math.ceil(seconds));

export const useCooldown = ({ initialSeconds = 0, seconds }: UseCooldownOptions) => {
    const durationSeconds = normalizeSeconds(seconds);
    const [expiresAt, setExpiresAt] = useState<number | null>(() => {
        const initialDuration = normalizeSeconds(initialSeconds);
        return initialDuration > 0 ? Date.now() + initialDuration * 1_000 : null;
    });
    const [remainingSeconds, setRemainingSeconds] = useState(() => normalizeSeconds(initialSeconds));

    useEffect(() => {
        if (expiresAt === null) {
            return;
        }

        const updateRemaining = () => {
            const nextRemaining = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1_000));
            setRemainingSeconds(nextRemaining);
            if (nextRemaining === 0) {
                setExpiresAt(null);
            }
        };

        updateRemaining();
        const interval = window.setInterval(updateRemaining, 250);

        return () => window.clearInterval(interval);
    }, [expiresAt]);

    const start = useCallback(
        (overrideSeconds?: number) => {
            const nextDuration = normalizeSeconds(overrideSeconds ?? durationSeconds);
            setRemainingSeconds(nextDuration);
            setExpiresAt(nextDuration > 0 ? Date.now() + nextDuration * 1_000 : null);
        },
        [durationSeconds],
    );

    const clear = useCallback(() => {
        setExpiresAt(null);
        setRemainingSeconds(0);
    }, []);

    return {
        clear,
        coolingDown: remainingSeconds > 0,
        remainingSeconds,
        start,
    };
};
